import React from 'react';

export default function PrivacyPolicy() {
  return (
    <div className="page">
      <div className="container" style={{ maxWidth: '860px', padding: '3rem 2rem' }}>
        <div className="page-header">
          <h1>Privacy Policy</h1>
          <p>Last updated: May 27, 2026</p>
        </div>

        <div className="static-content">
          {/* SECTION 1 — INTRODUCTION */}
          <section>
            <h2>1. Introduction</h2>
            <p>
              AstroA2Z ("Company", "we", "us", "our") is committed to protecting your privacy
              and personal data. This Privacy Policy explains how we collect, use, store, share,
              and protect your information when you use our platform (web application, mobile
              applications, and APIs — collectively, the "Platform").
            </p>
            <p>
              This policy is published in compliance with the Digital Personal Data Protection Act,
              2023 (DPDP Act), the Information Technology Act, 2000, and the Information Technology
              (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information)
              Rules, 2011.
            </p>
            <p>
              By using our Platform, you consent to the collection and processing of your data as
              described in this Policy. If you do not agree, please do not use our services.
            </p>
          </section>

          {/* SECTION 2 — DATA FIDUCIARY INFORMATION */}
          <section>
            <h2>2. Data Fiduciary Information</h2>
            <p>
              Under the DPDP Act, 2023, AstroA2Z acts as the "Data Fiduciary" responsible for
              processing your personal data:
            </p>
            <p>
              <strong>Entity:</strong> AstroA2Z<br />
              <strong>Registered Address:</strong> Hyderabad, Telangana, India<br />
              <strong>Data Protection Officer Email:</strong>{' '}
              <a href="mailto:dpo@astroa2z.com">dpo@astroa2z.com</a><br />
              <strong>Grievance Officer Email:</strong>{' '}
              <a href="mailto:grievance@astroa2z.com">grievance@astroa2z.com</a>
            </p>
          </section>

          {/* SECTION 3 — INFORMATION WE COLLECT */}
          <section>
            <h2>3. Information We Collect</h2>

            <h3>3.1 Information You Provide Directly</h3>
            <ul>
              <li><strong>Account Data:</strong> Name, email address, phone number, password (hashed)</li>
              <li>
                <strong>Birth Data:</strong> Date of birth, time of birth, place of birth — required
                for astrological calculations (kundali, horoscope, compatibility)
              </li>
              <li><strong>Profile Data:</strong> Gender, preferred language, profile photo (optional)</li>
              <li><strong>Payment Data:</strong> Processed by third-party payment gateways; we do NOT
                store full card numbers, CVV, or bank account details</li>
              <li><strong>Communication Data:</strong> Messages, feedback, support requests</li>
              <li><strong>Third-Party Birth Data:</strong> If you submit another person's birth data
                (e.g., for compatibility matching), you represent that you have their consent</li>
            </ul>

            <h3>3.2 Information Collected Automatically</h3>
            <ul>
              <li><strong>Device Data:</strong> Device type, operating system, browser type, screen resolution</li>
              <li><strong>Usage Data:</strong> Pages visited, features used, session duration, click patterns</li>
              <li><strong>Log Data:</strong> IP address, access timestamps, referral URLs, error logs</li>
              <li><strong>Location Data:</strong> Approximate location (city-level) derived from IP address;
                precise GPS location only if you explicitly grant permission</li>
              <li><strong>Cookies &amp; Tracking:</strong> See Section 9</li>
            </ul>

            <h3>3.3 Information from Third Parties</h3>
            <ul>
              <li>Social login providers (Google, Facebook) — name, email, profile picture</li>
              <li>Payment processors — transaction status, payment confirmation</li>
            </ul>
          </section>

          {/* SECTION 4 — PURPOSE OF DATA PROCESSING */}
          <section>
            <h2>4. Purpose of Data Processing (Purpose Limitation)</h2>
            <p>
              In compliance with the DPDP Act's purpose limitation principle, we process your
              personal data <strong>only</strong> for the following specified purposes:
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #ddd', textAlign: 'left' }}>
                  <th style={{ padding: '0.5rem' }}>Purpose</th>
                  <th style={{ padding: '0.5rem' }}>Data Used</th>
                  <th style={{ padding: '0.5rem' }}>Legal Basis</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '0.5rem' }}>Astrological calculations</td>
                  <td style={{ padding: '0.5rem' }}>Birth data</td>
                  <td style={{ padding: '0.5rem' }}>Consent</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '0.5rem' }}>Account management</td>
                  <td style={{ padding: '0.5rem' }}>Name, email, phone</td>
                  <td style={{ padding: '0.5rem' }}>Contract performance</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '0.5rem' }}>Payment processing</td>
                  <td style={{ padding: '0.5rem' }}>Transaction data</td>
                  <td style={{ padding: '0.5rem' }}>Contract performance</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '0.5rem' }}>Service improvement</td>
                  <td style={{ padding: '0.5rem' }}>Usage &amp; device data</td>
                  <td style={{ padding: '0.5rem' }}>Legitimate interest</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '0.5rem' }}>Customer support</td>
                  <td style={{ padding: '0.5rem' }}>Communication data</td>
                  <td style={{ padding: '0.5rem' }}>Consent / Contract</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '0.5rem' }}>Legal compliance</td>
                  <td style={{ padding: '0.5rem' }}>As required</td>
                  <td style={{ padding: '0.5rem' }}>Legal obligation</td>
                </tr>
                <tr>
                  <td style={{ padding: '0.5rem' }}>Marketing (opt-in only)</td>
                  <td style={{ padding: '0.5rem' }}>Email, preferences</td>
                  <td style={{ padding: '0.5rem' }}>Consent</td>
                </tr>
              </tbody>
            </table>
            <p style={{ marginTop: '1rem' }}>
              We will <strong>not</strong> process your data for any purpose beyond what is stated
              above without obtaining fresh consent.
            </p>
          </section>

          {/* SECTION 5 — AI DATA PROCESSING */}
          <section>
            <h2>5. AI &amp; Automated Processing Disclosure</h2>
            <p>
              Our Platform uses artificial intelligence (AI) and automated systems to generate
              astrological readings and interpretations. When you use AI-powered features:
            </p>
            <ul>
              <li>Your birth data and query inputs are processed by AI models to generate readings</li>
              <li>We may use anonymized, aggregated usage patterns to improve AI model accuracy</li>
              <li>AI processing does not involve human review of your individual data in real-time</li>
              <li>
                No automated decision-making with legal or significant effects is performed — all
                AI outputs are informational/entertainment only
              </li>
              <li>
                Your data is not used to train third-party AI models without your explicit consent
              </li>
            </ul>
          </section>

          {/* SECTION 6 — DATA SHARING */}
          <section>
            <h2>6. Data Sharing &amp; Disclosure</h2>
            <p>
              We do <strong>not</strong> sell your personal data. We may share data only in these
              limited circumstances:
            </p>

            <h3>6.1 Service Providers</h3>
            <p>
              Trusted third-party vendors who assist us in operating the Platform (hosting, payment
              processing, email delivery, analytics). All service providers are bound by data
              processing agreements.
            </p>

            <h3>6.2 Priests/Pandits (for Bookings)</h3>
            <p>
              If you book a priest, relevant booking details (name, date, puja type, location) are
              shared with the assigned priest. Birth data is shared only if the service requires it
              and you consent.
            </p>

            <h3>6.3 Legal Requirements</h3>
            <p>
              We may disclose data if required by law, court order, or government authority, or to
              protect our rights, safety, or property.
            </p>

            <h3>6.4 Business Transfers</h3>
            <p>
              In the event of a merger, acquisition, or sale of assets, user data may be transferred.
              You will be notified of any such change and your choices regarding your data.
            </p>
          </section>

          {/* SECTION 7 — DATA RETENTION */}
          <section>
            <h2>7. Data Retention &amp; Storage Limitation</h2>
            <p>We retain your data only as long as necessary:</p>
            <ul>
              <li><strong>Active accounts:</strong> Data retained for the duration of your account</li>
              <li><strong>Deleted accounts:</strong> Account deletion is a <strong>soft delete</strong>.
                Your account is immediately deactivated and hidden from all active services. Data is
                retained in an inactive state for 30 days to allow recovery, after which it is
                permanently and irreversibly purged from our systems</li>
              <li><strong>Payment records:</strong> Retained for 8 years as required under Indian tax law</li>
              <li><strong>Server logs:</strong> Retained for 90 days for security purposes</li>
              <li><strong>Anonymized analytics:</strong> May be retained indefinitely (non-personal data)</li>
            </ul>

            <h3>7.1 No Marketing Use After Deletion</h3>
            <p>
              <strong>Once your account is deleted (soft-deleted), your personal data will NOT be used
              for marketing, advertising, promotional communications, or any commercial purpose
              whatsoever.</strong> Soft-deleted data exists solely for the purpose of account recovery
              (if you change your mind within 30 days) and legal compliance obligations. After the
              30-day retention window, data is permanently erased.
            </p>

            <h3>7.2 Immediate Permanent Deletion</h3>
            <p>
              If you do not wish to wait 30 days and want immediate permanent deletion, you may
              email <a href="mailto:dpo@astroa2z.com">dpo@astroa2z.com</a> with the subject
              "Immediate Permanent Deletion" and we will process it within 72 hours.
            </p>
          </section>

          {/* SECTION 8 — DATA SECURITY */}
          <section>
            <h2>8. Data Security Measures</h2>
            <p>We implement industry-standard security measures including:</p>
            <ul>
              <li>AES-256 encryption for data at rest</li>
              <li>TLS 1.3 encryption for data in transit</li>
              <li>Bcrypt hashing for passwords (never stored in plain text)</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Access controls with principle of least privilege</li>
              <li>Secure backup procedures with encryption</li>
              <li>Incident response procedures for data breach scenarios</li>
            </ul>
            <p>
              In the event of a data breach affecting your personal data, we will notify you and
              the relevant authorities within 72 hours as required by applicable law.
            </p>
          </section>

          {/* SECTION 9 — COOKIES */}
          <section>
            <h2>9. Cookies &amp; Tracking Technologies</h2>
            <p>We use the following types of cookies:</p>
            <ul>
              <li>
                <strong>Essential Cookies:</strong> Required for Platform functionality (session
                management, authentication). Cannot be disabled.
              </li>
              <li>
                <strong>Analytics Cookies:</strong> Help us understand usage patterns (Google Analytics
                or similar). Can be disabled.
              </li>
              <li>
                <strong>Preference Cookies:</strong> Remember your settings (language, theme). Can be disabled.
              </li>
            </ul>
            <p>
              We do <strong>not</strong> use advertising or behavioral tracking cookies. You can
              manage cookie preferences through your browser settings or our cookie consent banner.
            </p>
          </section>

          {/* SECTION 10 — YOUR RIGHTS UNDER DPDP ACT */}
          <section>
            <h2>10. Your Rights (DPDP Act, 2023)</h2>
            <p>As a Data Principal under the DPDP Act, you have the following rights:</p>

            <h3>10.1 Right to Access</h3>
            <p>
              You may request a summary of your personal data being processed and the processing
              activities being carried out.
            </p>

            <h3>10.2 Right to Correction</h3>
            <p>
              You may request correction of inaccurate or incomplete personal data, and updating
              of outdated data.
            </p>

            <h3>10.3 Right to Erasure (Right to be Forgotten)</h3>
            <p>
              You may request complete deletion of your personal data from our systems, subject to:
            </p>
            <ul>
              <li>Legal retention requirements (tax records, regulatory compliance)</li>
              <li>Ongoing contractual obligations</li>
              <li>Legitimate interests in fraud prevention</li>
            </ul>
            <p>
              Upon a valid erasure request, we will delete your data within 30 days and confirm
              deletion in writing.
            </p>

            <h3>10.4 Right to Withdraw Consent</h3>
            <p>
              You may withdraw consent for data processing at any time. Withdrawal does not affect
              the lawfulness of processing done before withdrawal. To withdraw consent:
            </p>
            <ul>
              <li>Account settings → Privacy → Withdraw Consent, or</li>
              <li>Email <a href="mailto:dpo@astroa2z.com">dpo@astroa2z.com</a></li>
            </ul>

            <h3>10.5 Right to Grievance Redressal</h3>
            <p>
              If you are unsatisfied with our response to your data request, you may file a complaint
              with the Data Protection Board of India.
            </p>

            <h3>10.6 Right to Nominate</h3>
            <p>
              You may nominate another individual to exercise your data rights in the event of your
              death or incapacity, as provided under the DPDP Act.
            </p>
          </section>

          {/* SECTION 11 — HOW TO EXERCISE RIGHTS */}
          <section>
            <h2>11. How to Exercise Your Rights</h2>
            <p>To exercise any of the above rights:</p>
            <ol>
              <li>Email <a href="mailto:dpo@astroa2z.com">dpo@astroa2z.com</a> with subject
                "Data Rights Request"</li>
              <li>Include your registered email address and specify the right you wish to exercise</li>
              <li>We will verify your identity and respond within 7 days</li>
              <li>Requests will be fulfilled within 30 days of verification</li>
            </ol>
            <p>There is no fee for exercising your data rights.</p>
          </section>

          {/* SECTION 12 — CHILDREN'S DATA */}
          <section>
            <h2>12. Children's Data Protection</h2>
            <p>
              Our Platform is not intended for users under 18 years of age. We do not knowingly
              collect personal data from children. If you are a parent/guardian and believe your
              child has provided us with personal data, contact us at{' '}
              <a href="mailto:dpo@astroa2z.com">dpo@astroa2z.com</a> and we will promptly delete
              such data.
            </p>
            <p>
              If a user submits a minor's birth data (e.g., for a child's kundali), the parent or
              legal guardian must provide consent and assumes full responsibility.
            </p>
          </section>

          {/* SECTION 13 — CROSS-BORDER DATA TRANSFER */}
          <section>
            <h2>13. Cross-Border Data Transfers</h2>
            <p>
              Your data is primarily stored on servers located in India. If data transfer outside
              India is necessary (e.g., for AI processing or cloud services), such transfers will
              comply with the DPDP Act and applicable government notifications regarding permitted
              jurisdictions.
            </p>
          </section>

          {/* SECTION 14 — THIRD-PARTY LINKS */}
          <section>
            <h2>14. Third-Party Links &amp; Services</h2>
            <p>
              Our Platform may contain links to third-party websites or services. We are not
              responsible for the privacy practices of these third parties. We encourage you to
              review their privacy policies before providing any personal data.
            </p>
          </section>

          {/* SECTION 15 — DATA BREACH NOTIFICATION */}
          <section>
            <h2>15. Data Breach Notification</h2>
            <p>In the event of a personal data breach:</p>
            <ul>
              <li>We will notify the Data Protection Board of India within 72 hours</li>
              <li>Affected users will be notified via email within 72 hours</li>
              <li>The notification will include: nature of breach, data affected, remedial actions
                taken, and steps you can take to protect yourself</li>
              <li>We maintain a breach register as required by law</li>
            </ul>
          </section>

          {/* SECTION 16 — CHANGES TO POLICY */}
          <section>
            <h2>16. Changes to This Privacy Policy</h2>
            <p>
              <strong>AstroA2Z reserves the absolute and unconditional right to amend, modify,
              update, or replace this Privacy Policy at any point in time, at its sole discretion.</strong>
              Changes may reflect new data practices, regulatory requirements, service modifications,
              or any other reason we deem necessary.
            </p>
            <p>
              We will make reasonable efforts to notify you of material changes via email or a
              prominent notice on the Platform. The "Last updated" date at the top of this page
              will be revised accordingly. However, it is your sole responsibility to review this
              Policy periodically for updates.
            </p>
            <p>
              <strong>By continuing to access or use the Platform after any changes are posted,
              you are deemed to have accepted and consented to the revised Privacy Policy in its
              entirety.</strong> You are obligated to adhere to the most current version of this
              Policy at all times. If you do not agree to the modified Policy, your sole remedy is
              to immediately discontinue use of the Platform, withdraw your consent, and request
              deletion of your data.
            </p>
          </section>

          {/* SECTION 17 — CONTACT */}
          <section>
            <h2>17. Contact Us</h2>
            <p>
              For any privacy-related questions, concerns, or requests:<br />
              <strong>Data Protection Officer:</strong>{' '}
              <a href="mailto:dpo@astroa2z.com">dpo@astroa2z.com</a><br />
              <strong>Grievance Officer:</strong>{' '}
              <a href="mailto:grievance@astroa2z.com">grievance@astroa2z.com</a><br />
              <strong>General Support:</strong>{' '}
              <a href="mailto:privacy@astroa2z.com">privacy@astroa2z.com</a><br />
              <strong>Address:</strong> Hyderabad, Telangana, India
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
